import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  NumberInput,
} from '@mantine/core';
import { IconSitemap } from '@tabler/icons-react';
import { TaskModalBusyOverlay } from './TaskModalBusyOverlay';
import { DateInput } from '@mantine/dates';
import { useModalContainer } from '../../../contexts/ModalContainerContext';
import type { NewTaskFormValues } from '../../../gantt/addTaskUtils';
import {
  coerceNewTaskFormValues,
  computeDurationFromStartAndFinish,
  computeFinishFromStartAndDuration,
  defaultNewTaskFormValues,
} from '../../../gantt/addTaskUtils';
import type { TaskSnapshot } from '../../../stores/types';
import { resolveTaskName, parseDateSafe } from '../../../utils/utils';
import { GANTT_DATE_DISPLAY_FORMAT } from '../../../gantt/ganttDateFormat';
import { busyActionButtonProps } from './taskModalButtonStyles';
import { PredecessorTaskPickerPanel } from './predecessor-picker';

const TASK_TYPE_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'phase', label: 'Phase' },
  { value: 'location', label: 'Location' },
  { value: 'construction_activity', label: 'Construction Activity' },
  { value: 'preparatory_activity', label: 'Preparatory Activity' },
];

/** Synthetic id for validating predecessors against a not-yet-created task. */
const NEW_TASK_PICKER_TARGET_ID = '__add-task-draft__';

export interface AddTaskModalProps {
  opened: boolean;
  parentTask: TaskSnapshot | null;
  /** Existing schedule tasks for the predecessor picker. */
  tasks: TaskSnapshot[];
  submitting: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (values: NewTaskFormValues) => void | Promise<void>;
  resolvePredecessorWbsCode?: (taskId: string) => string | null;
}

export function AddTaskModal({
  opened,
  parentTask,
  tasks,
  submitting,
  submitError,
  onClose,
  onSubmit,
  resolvePredecessorWbsCode,
}: AddTaskModalProps) {
  const modalContainer = useModalContainer();
  const [form, setForm] = useState<NewTaskFormValues>(() => defaultNewTaskFormValues(parentTask));
  /** Set synchronously on click so the button disables before parent work runs. */
  const [actionPending, setActionPending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const busy = submitting || actionPending;

  const normalizeDateInput = (value: Date | string | null): Date | null => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    return parseDateSafe(String(value)) ?? null;
  };

  useEffect(() => {
    if (opened) {
      setForm(defaultNewTaskFormValues(parentTask));
      setPickerOpen(false);
    } else {
      setActionPending(false);
      setPickerOpen(false);
    }
  }, [opened, parentTask]);

  const pickerTasks = useMemo((): TaskSnapshot[] => {
    const draft = {
      id: NEW_TASK_PICKER_TARGET_ID,
      projectId: parentTask?.projectId ?? '',
      parentTaskId: parentTask?.id ?? null,
      taskName: form.name.trim() || 'New task',
      wbs: null,
      srNo: null,
      outlineLevel: null,
      outlinePosition: null,
      critical: false,
      milestone: form.milestone,
      summary: false,
      start: null,
      finish: null,
      duration: null,
      percentComplete: null,
      baselineInfo: [],
      taskType: form.taskType,
      locationType: null,
    } as unknown as TaskSnapshot;
    return [...tasks, draft];
  }, [tasks, parentTask, form.name, form.milestone, form.taskType]);

  const title = parentTask
    ? `Add child task under “${resolveTaskName(parentTask)}”`
    : 'Add root task';

  const submitForm = () => {
    if (!form.name.trim() || busy) return;
    setActionPending(true);
    void Promise.resolve(onSubmit(coerceNewTaskFormValues(form))).finally(() => {
      setActionPending(false);
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitForm();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Box>
            <Text fw={700} size="lg" lh={1.25}>
              {title}
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {parentTask
                ? 'Creates a new child under the selected row.'
                : 'Creates the first task in the project schedule.'}
            </Text>
          </Box>
        }
        centered
        size="lg"
        radius="md"
        padding="lg"
        closeOnClickOutside={!busy && !pickerOpen}
        closeOnEscape={!busy && !pickerOpen}
        portalProps={modalContainer ? { target: modalContainer } : undefined}
        styles={{
          header: {
            paddingBottom: 8,
            borderBottom: '1px solid var(--mantine-color-default-border)',
            marginBottom: 4,
          },
          body: {
            paddingTop: 16,
          },
        }}
      >
        <TaskModalBusyOverlay busy={busy} label="Creating new task..">
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Task name"
                placeholder="Enter task name"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
                data-autofocus
                radius="sm"
              />

              <Group grow align="flex-start">
                <DateInput
                  label="Start"
                  value={form.start}
                  valueFormat={GANTT_DATE_DISPLAY_FORMAT}
                  onChange={(d) => {
                    const start = normalizeDateInput(d);
                    if (!start) return;
                    setForm((f) => ({
                      ...f,
                      start,
                      finish: computeFinishFromStartAndDuration(start, f.duration),
                    }));
                  }}
                  radius="sm"
                />
                <NumberInput
                  label="Duration (days)"
                  min={0}
                  value={form.duration}
                  onChange={(v) => {
                    const duration = Number(v) || 0;
                    setForm((f) => ({
                      ...f,
                      duration,
                      finish: computeFinishFromStartAndDuration(f.start, duration),
                    }));
                  }}
                  radius="sm"
                />
              </Group>

              <DateInput
                label="Finish"
                value={form.finish}
                valueFormat={GANTT_DATE_DISPLAY_FORMAT}
                onChange={(d) => {
                  const finish = normalizeDateInput(d);
                  if (!finish) return;
                  setForm((f) => ({
                    ...f,
                    finish,
                    duration: computeDurationFromStartAndFinish(f.start, finish),
                  }));
                }}
                radius="sm"
              />

              <Divider
                label={
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
                    Dependencies
                  </Text>
                }
                labelPosition="left"
              />

              <Box>
                <TextInput
                  label="Predecessor"
                  placeholder="Sr No, WBS code, or task name (comma-separated)"
                  value={form.predecessors}
                  onChange={(e) => setForm((f) => ({ ...f, predecessors: e.currentTarget.value }))}
                  radius="sm"
                />
                <Group mt={6}>
                  <Button
                    size="compact-xs"
                    variant="light"
                    type="button"
                    leftSection={<IconSitemap size={12} />}
                    disabled={busy || tasks.length === 0}
                    onClick={() => setPickerOpen(true)}
                  >
                    Pick predecessors
                  </Button>
                </Group>
                <Text size="xs" c="dimmed" mt={6}>
                  Use Pick to choose tasks and set FS/SS/FF/SF + lag/lead, or type codes (e.g. 1.2FS-1).
                </Text>
              </Box>

              <Divider
                label={
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
                    Classification
                  </Text>
                }
                labelPosition="left"
              />

              <Select
                label="Task type"
                data={TASK_TYPE_OPTIONS}
                value={form.taskType}
                onChange={(v) => setForm((f) => ({ ...f, taskType: v ?? 'construction_activity' }))}
                radius="sm"
                comboboxProps={{ withinPortal: true, zIndex: 1100 }}
              />

              <Checkbox
                label="Milestone"
                checked={form.milestone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, milestone: e.currentTarget.checked }))
                }
              />

              {submitError ? (
                <Text size="sm" c="red">
                  {submitError}
                </Text>
              ) : null}

              <Group justify="flex-end" gap="sm" mt="xs">
                <Button variant="default" onClick={onClose} disabled={busy} type="button" radius="sm">
                  Cancel
                </Button>
                <Button
                  type="button"
                  loading={busy}
                  disabled={!form.name.trim() || busy}
                  onClick={submitForm}
                  radius="sm"
                  {...busyActionButtonProps(busy)}
                >
                  Create task
                </Button>
              </Group>
            </Stack>
          </form>
        </TaskModalBusyOverlay>
      </Modal>

      <PredecessorTaskPickerPanel
        opened={opened && pickerOpen}
        onClose={() => setPickerOpen(false)}
        targetTaskId={NEW_TASK_PICKER_TARGET_ID}
        tasks={pickerTasks}
        currentInput={form.predecessors}
        onApplyInput={(input) => setForm((f) => ({ ...f, predecessors: input }))}
        resolveWbsCode={resolvePredecessorWbsCode}
        title="Pick predecessors"
        presentation="modal"
      />
    </>
  );
}
